import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, ShieldCheck, Lock } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
  const { t } = useLang();
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/settings/plans`)
      .then(({ data }) => setPlans(data.plans || []))
      .catch((e) => console.error("plans failed", e));
  }, []);

  const scrollToContact = (planKey) => {
    const el = document.getElementById("contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
      // Preselect via query on hash
      window.history.replaceState(null, "", `#contact?plan=${planKey}`);
      window.dispatchEvent(new CustomEvent("hakan-plan-select", { detail: planKey }));
    }
  };

  return (
    <section
      id="plans"
      data-testid="plans-section"
      className="py-20 md:py-32 border-t border-white/10 bg-[var(--hk-surface)]"
    >
      <div className="container-hk">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14 md:mb-20">
          <div>
            <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold mb-3">
              // 003 · {t.plans.overline}
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl uppercase leading-[0.95] tracking-tight">
              {t.plans.title}
            </h2>
          </div>
          <p className="text-sm text-white/60 max-w-sm">{t.plans.subtitle}</p>
        </div>

        {/* Promotional guarantee banner */}
        <motion.div
          data-testid="promo-guarantee"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
          className="relative mb-14 md:mb-16 overflow-hidden border border-[var(--hk-red)]/60 bg-gradient-to-r from-[var(--hk-red)]/10 via-black to-black"
        >
          {/* accent bars */}
          <div aria-hidden className="absolute left-0 top-0 bottom-0 w-1 bg-[var(--hk-red)]" />
          <div
            aria-hidden
            className="absolute -right-16 -top-16 w-56 h-56 rounded-full opacity-20"
            style={{ background: "radial-gradient(circle, rgba(227,10,23,0.7), transparent 60%)" }}
          />
          <div className="relative flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6 p-6 md:p-8">
            <div className="flex items-center gap-3 md:gap-4 shrink-0">
              <div className="flex items-center justify-center w-12 h-12 md:w-14 md:h-14 bg-[var(--hk-red)]/15 border border-[var(--hk-red)]/40">
                <ShieldCheck size={26} className="text-[var(--hk-red)]" strokeWidth={1.75} />
              </div>
              <div className="text-[10px] md:text-xs tracking-[0.35em] text-[var(--hk-red)] font-bold">
                {t.plans.guarantee_overline}
              </div>
            </div>
            <div className="hidden md:block h-12 w-px bg-white/15" />
            <div className="flex-1">
              <p className="font-display text-xl md:text-2xl lg:text-3xl uppercase tracking-tight leading-tight text-white">
                {t.plans.guarantee_before} <span className="text-[var(--hk-red)]">{t.plans.guarantee_highlight}</span> {t.plans.guarantee_after}
              </p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {plans.map((p, i) => {
            const highlighted = p.badge === "best" && p.available;
            const unavailable = !p.available;
            const days = parseInt(p.name, 10);
            return (
              <motion.div
                key={p.key}
                data-testid={`plan-${p.key}`}
                data-available={p.available ? "true" : "false"}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ delay: i * 0.1, duration: 0.7 }}
                className={`relative bg-black p-8 md:p-10 border ${
                  highlighted
                    ? "border-[var(--hk-red)] card-highlight"
                    : "border-white/10"
                } flex flex-col ${unavailable ? "opacity-60 grayscale" : ""}`}
              >
                {unavailable ? (
                  <div
                    data-testid={`badge-unavailable-${p.key}`}
                    className="absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold tracking-[0.3em] bg-white/15 text-white/80 border border-white/20 flex items-center gap-1.5"
                  >
                    <Lock size={10} /> {t.plans.not_available}
                  </div>
                ) : (
                  p.badge && (
                    <div
                      data-testid={`badge-${p.key}`}
                      className={`absolute -top-3 left-6 px-3 py-1 text-[10px] font-bold tracking-[0.3em] ${
                        highlighted
                          ? "bg-[var(--hk-red)] text-white"
                          : "bg-white text-black"
                      }`}
                    >
                      {p.badge === "best" ? t.plans.best_value : t.plans.most_popular}
                    </div>
                  )
                )}
                <div className="text-[10px] tracking-[0.35em] text-white/50 mb-4">
                  {t.plans.overline}
                </div>
                <div className="font-display text-6xl md:text-7xl uppercase leading-none">
                  {p.name}
                  <span className="text-[var(--hk-red)]">.</span>
                </div>
                <div className="text-sm tracking-[0.3em] text-white/70 mt-2 uppercase">
                  {p.unit}
                </div>

                <div className="my-8 hr-dot" />

                <div className="flex items-baseline gap-2 mb-8">
                  <span className="font-display text-5xl md:text-6xl">
                    €{p.price}
                  </span>
                  <span className="text-xs tracking-[0.3em] text-white/50">
                    {t.plans.currency}
                  </span>
                </div>

                {p.description && (
                  <p data-testid={`plan-desc-${p.key}`} className="text-sm text-white/70 leading-relaxed -mt-4 mb-8">
                    {p.description}
                  </p>
                )}

                <ul className="space-y-3 mb-10 flex-1">
                  {(Array.isArray(p.features)
                    ? p.features
                    : [
                        t.plans.features.tips(Number.isNaN(days) ? p.name : days),
                        t.plans.features.analysis,
                        t.plans.features.odds,
                        t.plans.features.stake,
                        t.plans.features.support,
                        t.plans.features.replacement,
                      ]
                  ).map((f, idx) => (
                    <li
                      key={idx}
                      data-testid={`plan-feature-${p.key}-${idx}`}
                      className="flex items-start gap-3 text-sm text-white/75"
                    >
                      <Check
                        size={16}
                        className="mt-0.5 shrink-0 text-[var(--hk-red)]"
                      />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <button
                  data-testid={`plan-cta-${p.key}`}
                  onClick={() => !unavailable && scrollToContact(p.key)}
                  disabled={unavailable}
                  className={`w-full py-4 text-xs font-bold tracking-[0.3em] transition-colors ${
                    unavailable
                      ? "border border-white/15 text-white/40 cursor-not-allowed"
                      : highlighted
                      ? "btn-red"
                      : "btn-outline hover:bg-white hover:text-black"
                  }`}
                >
                  {unavailable ? t.plans.not_available : `${t.plans.cta} →`}
                </button>
              </motion.div>
            );
          })}
        </div>

        <p className="mt-10 text-xs tracking-[0.15em] text-white/40 text-center uppercase">
          {t.plans.note}
        </p>
      </div>
    </section>
  );
};

export default Pricing;
